import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Group,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle } from '@tabler/icons-react';
import type { GroupType, SchoolYear } from '@pta/types';
import { createGroup, fetchGroupTypes, fetchSchoolYears } from '../api/groups';
import { ApiError } from '../api/client';

interface GroupCreateModalProps {
  opened: boolean;
  onClose: () => void;
  /** A felhasználó országkódja — ha nincs, default 'HU'. Az MVP egyelőre csak HU. */
  countryCode?: string;
  onCreated: (groupId: number) => void;
}

const fullSpan = { gridColumn: '1 / -1' };

/**
 * Free_text csoport-létrehozó modal (nyitott rendszer, 2026-04-26 modell).
 *
 * Két lépés / wizard helyett egy form: a felhasználó megadja az iskola nevét
 * szabad szövegként (`institutionFreeText`), majd a csoport nevét + típusát +
 * tanévét. A BE eldönti a binding_status-t (`'free_text'`) és auto-leader
 * role-t grantel.
 *
 * Az institution-bound (claimed/verified) flow külön, admin role-okhoz a FEA-n
 * van — itt nem érhető el.
 */
export function GroupCreateModal({
  opened,
  onClose,
  countryCode = 'HU',
  onCreated,
}: GroupCreateModalProps) {
  const [institutionFreeText, setInstitutionFreeText] = useState('');
  const [name, setName] = useState('');
  const [schoolYearId, setSchoolYearId] = useState<string | null>(null);
  const [groupTypeId, setGroupTypeId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [schoolYears, setSchoolYears] = useState<SchoolYear[]>([]);
  const [groupTypes, setGroupTypes] = useState<GroupType[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!opened) return;
    setInstitutionFreeText('');
    setName('');
    setSchoolYearId(null);
    setGroupTypeId(null);
    setNotes('');

    let cancelled = false;
    setLoadingMeta(true);
    void (async () => {
      try {
        const [years, types] = await Promise.all([
          fetchSchoolYears(countryCode),
          fetchGroupTypes(countryCode),
        ]);
        if (cancelled) return;
        setSchoolYears(years);
        setGroupTypes(types);
        // Default: az aktuális tanév (vagy a legutolsó), és az első csoporttípus.
        const currentYear = years.find((y) => y.isCurrent) ?? years[0];
        if (currentYear) setSchoolYearId(String(currentYear.id));
        if (types[0]) setGroupTypeId(String(types[0].id));
      } catch (e) {
        notifications.show({
          color: 'red',
          message: `Törzsadat betöltési hiba: ${e instanceof Error ? e.message : String(e)}`,
        });
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opened, countryCode]);

  const canSave =
    !saving &&
    !loadingMeta &&
    institutionFreeText.trim().length > 0 &&
    name.trim().length > 0 &&
    schoolYearId !== null &&
    groupTypeId !== null;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const created = await createGroup({
        institutionFreeText: institutionFreeText.trim(),
        institutionBindingStatus: 'free_text',
        schoolYearId: Number(schoolYearId),
        groupTypeId: Number(groupTypeId),
        name: name.trim(),
        notes: notes.trim() || null,
      });
      notifications.show({ color: 'green', message: 'Csoport létrehozva.' });
      onCreated(created.id);
    } catch (e) {
      notifications.show({
        color: 'red',
        message: e instanceof ApiError ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Új csoport létrehozása"
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      size="lg"
    >
      <Stack>
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
          <Text size="sm">
            A csoport létrehozása után automatikusan <strong>csoportvezető</strong> jogot
            kapsz rá. Az iskolát szabad szövegként adhatod meg; a rendszer később
            engedi hivatalos intézményhez kötni (claim-folyamat).
          </Text>
        </Alert>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="sm">
          <TextInput
            style={fullSpan}
            label="Iskola neve"
            placeholder="pl. Petőfi Sándor Általános Iskola"
            value={institutionFreeText}
            onChange={(e) => setInstitutionFreeText(e.currentTarget.value)}
            maxLength={500}
            required
          />

          <TextInput
            label="Csoport / osztály neve"
            placeholder="pl. 5.a"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            maxLength={100}
            required
          />

          <Select
            label="Tanév"
            data={schoolYears.map((y) => ({
              value: String(y.id),
              label: y.displayName + (y.isCurrent ? ' (aktuális)' : ''),
            }))}
            value={schoolYearId}
            onChange={setSchoolYearId}
            disabled={loadingMeta || schoolYears.length === 0}
            allowDeselect={false}
            required
          />

          <Select
            style={fullSpan}
            label="Csoport típusa"
            data={groupTypes.map((t) => ({
              value: String(t.id),
              label: t.nameKey,
            }))}
            value={groupTypeId}
            onChange={setGroupTypeId}
            disabled={loadingMeta || groupTypes.length === 0}
            allowDeselect={false}
            required
          />

          <Textarea
            style={fullSpan}
            label="Megjegyzés (opcionális)"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            autosize
            minRows={2}
            maxLength={2000}
          />
        </SimpleGrid>

        <Group justify="end">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Mégse
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={!canSave}>
            Létrehozás
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
